import { useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { IconButton, IconCross, Autocomplete2 } from '@flodesk/grain';

const slideIn = keyframes`
  from { transform: translateX(16px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(16px); opacity: 0; }
`;

const backdropStyles = css`
  position: fixed;
  inset: 0;
  z-index: 100;
`;

const panelStyles = css`
  position: absolute;
  top: 16px;
  right: 16px;
  bottom: 16px;
  width: 620px;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-l);
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
    0px 5.88px 14.05px -1.94px rgba(0, 0, 0, 0.09),
    0px 23.29px 85.96px -14px rgba(0, 0, 0, 0.19);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} var(--grn-transition-fast) ease-out;

  &[data-closing='true'] {
    animation: ${slideOut} var(--grn-transition-fast) ease-in forwards;
  }
`;

const closeButtonStyles = css`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 4;
`;

const headerStyles = css`
  padding: 40px 40px 16px;
  background: white;
  z-index: 2;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.6px;
  color: var(--grn-color-content);
`;

const bodyStyles = css`
  flex: 1;
  overflow-y: auto;
  padding: 8px 40px 40px;
`;

const filterGroupStyles = css`
  position: relative;
  padding-top: 18px;
`;

const filterContainerStyles = css`
  border: 1px solid var(--grn-color-border);
  border-radius: var(--grn-radius-l);
  padding: 40px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const filterSectionStyles = css`
  background: var(--grn-color-background2);
  border-radius: var(--grn-radius-l);
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const sectionLabelStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const chipContainerStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 460px;
`;

const chipStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 16px;
  border-radius: 32px;
  border: 1px solid var(--grn-color-fade3);
  background: #f8f7f7;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: default;
`;

const chipSelectedStyles = css`
  ${chipStyles};
  background: #eae8e7;
  border-color: var(--grn-color-shade12);
`;

const inlineFieldRowStyles = css`
  display: flex;
  gap: 12px;
`;

const numberFieldStyles = css`
  width: 88px;
  flex-shrink: 0;
`;

const growFieldStyles = css`
  flex: 1;
  min-width: 0;
`;

const matchingHeaderStyles = css`
  position: absolute;
  top: 0;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  background: var(--grn-color-background);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  z-index: 1;
`;

const toggleGroupStyles = css`
  display: flex;
  height: 36px;
  background: var(--grn-color-background3);
  border-radius: var(--grn-radius-m);
  overflow: hidden;
`;

const toggleActiveStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: var(--grn-color-shade1);
  border: 3px solid var(--grn-color-background3);
  border-radius: var(--grn-radius-m);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: default;
`;

const toggleInactiveStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border: 3px solid transparent;
  border-radius: var(--grn-radius-m);
  background: transparent;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  cursor: default;
`;

const STATUSES = [
  'Active',
  'Unconfirmed',
  'Archived',
  'Bounced',
  'Unsubscribed',
  'Marked spam',
  'Cleaned',
];

type SegmentFilterModalProps = {
  days: number;
  onClose: () => void;
};

export default function SegmentFilterModal({ days, onClose }: SegmentFilterModalProps) {
  const [closing, setClosing] = useState(false);
  const timeframeLabel = `In the last ${days} days`;

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 200);
  };

  return (
    <div css={backdropStyles} role="presentation" onClick={handleClose}>
      <div
        css={panelStyles}
        data-closing={closing}
        className="grn-context"
        role="presentation"
        onClick={e => e.stopPropagation()}
      >
        <div css={closeButtonStyles}>
          <IconButton icon={<IconCross />} onClick={handleClose} />
        </div>

        <div css={headerStyles}>Filter your subscribers</div>

        <div css={bodyStyles}>
          <div css={filterGroupStyles}>
            <div css={matchingHeaderStyles}>
              <span>Matching</span>
              <div css={toggleGroupStyles}>
                <button type="button" css={toggleActiveStyles}>
                  all
                </button>
                <button type="button" css={toggleInactiveStyles}>
                  any
                </button>
              </div>
              <span>of these:</span>
            </div>

            <div css={filterContainerStyles}>
              <div css={filterSectionStyles}>
                <div css={sectionLabelStyles}>Status</div>
                <div css={chipContainerStyles}>
                  {STATUSES.map(status => (
                    <div key={status} css={status === 'Active' ? chipSelectedStyles : chipStyles}>
                      {status}
                    </div>
                  ))}
                </div>
              </div>

              <div css={filterSectionStyles}>
                <div css={sectionLabelStyles}>Number of emails opened</div>
                <Autocomplete2
                  isDisabled
                  value="is-equal-to"
                  options={[{ value: 'is-equal-to', content: 'Is equal to' }]}
                  onChange={() => undefined}
                />
                <div css={inlineFieldRowStyles}>
                  <div css={numberFieldStyles}>
                    <Autocomplete2
                      isDisabled
                      value="0"
                      options={[{ value: '0', content: '0' }]}
                      onChange={() => undefined}
                    />
                  </div>
                  <div css={growFieldStyles}>
                    <Autocomplete2
                      isDisabled
                      value="timeframe"
                      options={[{ value: 'timeframe', content: timeframeLabel }]}
                      onChange={() => undefined}
                    />
                  </div>
                </div>
              </div>

              <div css={filterSectionStyles}>
                <div css={sectionLabelStyles}>Number of emails clicked</div>
                <Autocomplete2
                  isDisabled
                  value="is-equal-to"
                  options={[{ value: 'is-equal-to', content: 'Is equal to' }]}
                  onChange={() => undefined}
                />
                <div css={inlineFieldRowStyles}>
                  <div css={numberFieldStyles}>
                    <Autocomplete2
                      isDisabled
                      value="0"
                      options={[{ value: '0', content: '0' }]}
                      onChange={() => undefined}
                    />
                  </div>
                  <div css={growFieldStyles}>
                    <Autocomplete2
                      isDisabled
                      value="timeframe"
                      options={[{ value: 'timeframe', content: timeframeLabel }]}
                      onChange={() => undefined}
                    />
                  </div>
                </div>
              </div>

              <div css={filterSectionStyles}>
                <div css={sectionLabelStyles}>Number of emails delivered</div>
                <Autocomplete2
                  isDisabled
                  value="is-more-than"
                  options={[{ value: 'is-more-than', content: 'Is more than' }]}
                  onChange={() => undefined}
                />
                <div css={inlineFieldRowStyles}>
                  <div css={numberFieldStyles}>
                    <Autocomplete2
                      isDisabled
                      value="3"
                      options={[{ value: '3', content: '3' }]}
                      onChange={() => undefined}
                    />
                  </div>
                  <div css={growFieldStyles}>
                    <Autocomplete2
                      isDisabled
                      value="timeframe"
                      options={[{ value: 'timeframe', content: timeframeLabel }]}
                      onChange={() => undefined}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
